/**
 * RecoverAccountPanel — "ik weet mijn wachtwoord niet meer".
 *
 * Twee vormen in één component, omdat het voor de gebruiker één vraag is maar
 * technisch twee verschillende routes:
 *
 *   - leerling  → gebruikersnaam + herstelcode + nieuw wachtwoord, via de
 *                 Edge Function. Geen e-mail in beeld; die heeft een kind niet.
 *   - volwassene → e-mailadres, via een herstelmail.
 *
 * De leerlingvariant vraagt bewust meteen om het nieuwe wachtwoord in plaats
 * van eerst te verifiëren en dán te vragen. Een tussenstap zou betekenen dat
 * een geverifieerde code even "los" geldig is, en dat is precies het venster
 * waarin iemand anders het account kan overnemen.
 */

import React, { useState } from 'react';
import { KeyRound, Mail, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { recoverLearnerAccount, recoverAdultAccount, MIN_PASSWORD_LENGTH } from '../../data/supabase/accountRecovery';
interface RecoverAccountPanelProps {
  /** Bepaalt welke route getoond wordt. */
  mode: 'learner' | 'adult';
  /** Terug naar het inlogformulier. */
  onBack: () => void;
  /** Voorvullen vanuit het inlogformulier, zodat niemand twee keer typt. */
  initialIdentifier?: string;
}
export function RecoverAccountPanel({
  mode,
  onBack,
  initialIdentifier = ''
}: RecoverAccountPanelProps) {
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = mode === 'learner' ? await recoverLearnerAccount({
        username: identifier,
        code,
        newPassword
      }) : await recoverAdultAccount(identifier);
      if (!result.ok) {
        setError(result.message ?? 'Er ging iets mis. Probeer het opnieuw.');
        return;
      }
      setDone(true);
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return <div className="space-y-4 text-center" role="status" aria-live="polite" data-id="element-302" data-mp="7kksq8yx94cj">
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" data-id="element-303" data-mp="3p12n9iincg6h" />
        <p className="font-bold text-slate-900" data-id="element-304" data-mp="3tyb9pvatv6dj">
          {mode === 'learner' ? 'Gelukt! Je hebt een nieuw wachtwoord.' : 'Als dit adres bij ons bekend is, staat er een mail voor je klaar.'}
        </p>
        <p className="text-sm text-slate-500 font-medium" data-id="element-305" data-mp="qidx7fp71mc1">
          {mode === 'learner' ? 'Log nu in met je gebruikersnaam en je nieuwe wachtwoord.' : 'Kijk ook even in je map met ongewenste post.'}
        </p>
        <button type="button" onClick={onBack} className="w-full rounded-xl bg-orange-500 px-4 py-3 font-bold text-white hover:bg-orange-600 transition-colors" data-id="element-306" data-mp="giwu961btmjr">
          
          Naar inloggen
        </button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-4" data-id="element-307" data-mp="31bgrz65eal3r">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800" data-id="element-308" data-mp="1jbr5s8if9nvy">
        
        <ArrowLeft className="w-4 h-4" data-id="element-309" data-mp="1eftnhwnlm7gj" />
        Terug naar inloggen
      </button>

      <div className="flex items-start gap-2 rounded-2xl border border-sky-200 bg-sky-50 p-3" data-id="element-310" data-mp="1sb4hl7u0qoju">
        {mode === 'learner' ? <KeyRound className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" data-id="element-311" data-mp="10m2eqqhoyrlv" /> : <Mail className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" data-id="element-312" data-mp="10mkmokdpuu9m" />}
        <p className="text-sm text-sky-900 font-medium" data-id="element-313" data-mp="vo0wu8w04ybz">
          {mode === 'learner' ? 'Gebruik je geheime herstelcode van zes woorden. Die kreeg je toen je je account maakte.' : 'We sturen je een mail waarmee je een nieuw wachtwoord kunt instellen.'}
        </p>
      </div>

      <div data-id="element-314" data-mp="gxjw6rr6ycg5">
        <label htmlFor="recover-identifier" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-315" data-mp="gxa32vaunp68">
          
          {mode === 'learner' ? 'Je gebruikersnaam' : 'Je e-mailadres'}
        </label>
        <input id="recover-identifier" type={mode === 'learner' ? 'text' : 'email'} autoComplete={mode === 'learner' ? 'username' : 'email'} value={identifier} onChange={e => setIdentifier(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 font-medium text-slate-900 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100" required data-id="element-316" data-mp="pmh46rfq1hos" />
        
      </div>

      {mode === 'learner' && <>
          <div data-id="element-317" data-mp="19briq23qdwlb">
            <label htmlFor="recover-code" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-318" data-mp="19c1cm0rwcnwa">
            
              Je herstelcode
            </label>
            <input id="recover-code" type="text" autoComplete="off" spellCheck={false} value={code} onChange={e => setCode(e.target.value)} placeholder="otter-kompas-tulp-raket-schelp-uil" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 font-mono text-sm text-slate-900 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100" required data-id="element-319" data-mp="1iawgh67gxlwf" />
          
            <p className="mt-1 text-xs text-slate-500 font-medium" data-id="element-320" data-mp="3hi3g9k935xac">
              Zes woorden met streepjes ertussen. Hoofdletters maken niet uit.
            </p>
          </div>

          <div data-id="element-321" data-mp="37qpa4tmmpz88">
            <label htmlFor="recover-password" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-322" data-mp="37sjti3i95ja7">
            
              Je nieuwe wachtwoord
            </label>
            <input id="recover-password" type="password" autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} minLength={MIN_PASSWORD_LENGTH} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 font-medium text-slate-900 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100" required data-id="element-323" data-mp="4bbh3z6anbmv" />
          
            <p className="mt-1 text-xs text-slate-500 font-medium" data-id="element-324" data-mp="3ll9mirw77ogt">
              Minimaal {MIN_PASSWORD_LENGTH} tekens.
            </p>
          </div>
        </>}

      {error && <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700" role="alert" aria-live="assertive" data-id="element-325" data-mp="2a2f5s7zdezdy">
        
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-326" data-mp="164v0u77r012s" />
          <span data-id="element-327" data-mp="s6dfgg5bf0o1">{error}</span>
        </div>}

      <button type="submit" disabled={busy} className="w-full rounded-xl bg-orange-500 px-4 py-3 font-bold text-white hover:bg-orange-600 disabled:opacity-60 transition-colors" data-id="element-328" data-mp="s5bvtna8h5up">
        
        {busy ? 'Even geduld…' : mode === 'learner' ? 'Nieuw wachtwoord instellen' : 'Stuur mij een herstelmail'}
      </button>
    </form>;
}